import type { DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { forwardRef, type CSSProperties, type PropsWithChildren } from 'react';
import { logger } from 'shiver-core/logger';

type SortableListFieldProps = {
  sortStrategy: 'verticalList' | 'rect';
  ids: UniqueIdentifier[];
  onChange: (value: UniqueIdentifier[]) => void;
  renderItem: (id: UniqueIdentifier, index: number) => React.ReactNode;
};

export const SortableList = forwardRef<HTMLDivElement, SortableListFieldProps>(
  (
    {
      ids,
      onChange,
      renderItem,
      sortStrategy,

      ...props
    },
    ref,
  ) => {
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          // デフォルトだとonClickなどpointer系のイベントを奪ってしまうので、
          // onPointerDown以降で5px動いたらドラッグ開始と判定
          distance: 5,
        },
      }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);

        if (oldIndex === -1 || newIndex === -1) {
          logger.warn('Invalid drag and drop operation');
          return;
        }

        const newItems = arrayMove(ids, oldIndex, newIndex);

        onChange(newItems);
      }
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={ids}
          strategy={
            sortStrategy === 'verticalList' ? verticalListSortingStrategy : rectSortingStrategy
          }
        >
          {ids.map((id, index) => {
            return (
              <SortableItem key={id} id={id}>
                {renderItem(id, index)}
              </SortableItem>
            );
          })}
        </SortableContext>
      </DndContext>
    );
  },
) as (props: SortableListFieldProps & { ref?: React.ForwardedRef<HTMLDivElement> }) => JSX.Element;

export const SortableItem = (props: PropsWithChildren<{ id: UniqueIdentifier }>) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: props.id,
  });

  if (transform) {
    // NOTE: drag中の要素が水平方向に歪むので変形させない
    transform.scaleX = 1;
  }

  const style: CSSProperties = {
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 1 : undefined,
    transform: CSS.Transform.toString(transform),
    // 水平方向に変な動き方をするので遷移させない
    // transition
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {props.children}
    </div>
  );
};
